import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Button } from '@/shared/ui';

/**
 * La pantalla de una dirección que no existe.
 *
 * **Sustituye a un redirect silencioso**, que es lo que había: una URL mal
 * escrita llevaba al inicio sin decir nada y borraba de la barra justo lo que
 * hacía falta leer para ver la errata. Y dentro del panel no había ni eso —
 * ninguna ruta casaba, el layout no llegaba a montarse y la página quedaba
 * **en blanco**, que se lee como que la aplicación se rompió.
 *
 * Por eso se enseña la ruta que falló. Es el dato que convierte «no funciona»
 * en «me faltó una letra», y el único que esta pantalla conoce.
 *
 * Se monta en tres sitios y a propósito no sabe en cuál está: suelta en la raíz
 * y **dentro** de los dos shells, para que una dirección mala en el panel
 * conserve el menú y en campo la barra de abajo. Un 404 a pantalla completa
 * desde dentro de la aplicación se siente como haber sido expulsado de ella.
 */
export default function NotFound() {
  const { pathname, key } = useLocation();
  const navigate = useNavigate();

  // `key` vale «default» sólo en la primera entrada del historial: se llegó
  // aquí escribiendo la dirección o desde fuera, y no hay atrás al que volver
  // que siga dentro del producto. Ofrecerlo sacaría de la aplicación, que es
  // lo contrario de lo que promete el botón.
  const sePuedeVolver = key !== 'default';

  return (
    // Dos formas de llenar el alto, porque son dos padres distintos: dentro de
    // los shells el contenedor de scroll tiene altura definida y manda
    // `h-full`; suelta en la raíz, el padre es la columna flex de `App` con
    // `min-h-full`, donde el porcentaje no resuelve y quien centra es `flex-1`.
    // Con una sola de las dos, la página queda pegada arriba en el otro sitio.
    <div className="flex h-full min-h-[24rem] flex-1 flex-col items-center justify-center gap-5 p-6 text-center">
      <p className="text-6xl font-semibold leading-none tabular-nums text-content-subtle">404</p>

      <div className="space-y-1">
        <h1 className="text-xl font-semibold">Esta página no existe</h1>
        <p className="mx-auto max-w-md text-sm text-content-muted">
          Puede que el enlace esté mal escrito, o que lo que había aquí se haya movido.
        </p>
      </div>

      {/* `break-all` y no `truncate`: la mitad interesante de una ruta larga
          suele ser el final, y cortarla con puntos suspensivos escondería justo
          la palabra con la errata.

          Y con borde, que no es adorno: dentro del panel el fondo de la página
          **ya es** `surface-sunken`, así que sólo con el relleno la píldora
          desaparecía y la ruta quedaba flotando como un párrafo más. La línea
          es lo único que la dibuja en los dos sitios donde se monta. */}
      <p className="max-w-full break-all rounded-lg border border-line bg-surface-sunken px-3 py-1.5 font-mono text-xs text-content-subtle">
        {pathname}
      </p>

      <div className="flex flex-wrap justify-center gap-2">
        {sePuedeVolver && (
          <Button variant="secondary" onClick={() => navigate(-1)}>
            Volver atrás
          </Button>
        )}
        {/* Al inicio y no a una sección concreta: esta pantalla vive en la raíz,
            en el panel y en campo, y `/` es lo único que acierta en los tres —
            `RoleRedirect` manda a cada rol a su sitio. */}
        <Link to="/">
          <Button>Ir al inicio</Button>
        </Link>
      </div>
    </div>
  );
}
